import { useState } from 'react';

import { Button, Icon, IconButton } from '@/components/ui';

interface TokenRevealBannerProps {
  raw: string;
  onDismiss: () => void;
}

/**
 * One-time-only display of a freshly minted PAT. The server keeps only a hash,
 * so this is the user's single chance to copy it. Includes a clear warning
 * banner styled in brand-soft so it's hard to miss.
 */
export function TokenRevealBanner({ raw, onDismiss }: TokenRevealBannerProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail in non-secure contexts; user can still select+copy.
    }
  };

  return (
    <div
      style={{
        background: 'var(--brand-soft)',
        border: '1px solid rgba(31,71,171,0.25)',
        borderRadius: 'var(--radius-md)',
        padding: 14,
        marginBottom: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
      role="alert"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: 'var(--brand-ink)',
          fontFamily: 'var(--font-ui)',
          fontWeight: 600,
        }}
      >
        <Icon name="shield" size={14} />
        <span>Your new token — copy it now</span>
        <IconButton
          icon="x"
          label="Dismiss"
          size="sm"
          iconSize={13}
          style={{ marginLeft: 'auto', color: 'var(--brand-ink)' }}
          onClick={onDismiss}
        />
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--brand-ink)' }}>
        We only store a hash — this exact value won&apos;t be shown again. Paste it
        into the LinkedIn Chrome extension&apos;s settings popup before dismissing
        this banner.
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          padding: '8px 10px',
        }}
      >
        <code
          style={{
            flex: 1,
            fontFamily: 'var(--font-mono)',
            fontSize: 12.5,
            wordBreak: 'break-all',
            color: 'var(--text)',
          }}
        >
          {raw}
        </code>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Icon name={copied ? 'check' : 'link'} size={12} />}
          onClick={() => void copy()}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}
